<template>
  <div class="page">
    <PageHeader>
      <span>Баланс</span>
      <span v-if="balanceAccount && balanceAccount[0]">
        {{ balanceAccount[0]._id }}
        {{ balanceAccount[0].title }}
        <strong>
          {{ balanceAccount[0].balance }}
          {{ balanceAccount[0].valute && balanceAccount[0].valute.name }}
        </strong>
      </span>
    </PageHeader>
    <div class="content" v-if="loaded">
      <div v-if="!dataLoadFailed">
        <el-card
          shadow="always"
          :body-style="{ padding: '20px' }"
          class="reconciliation-card"
        >
          <div slot="header" class="reconciliation-card__header">
            <span>Сверка баланса</span>
            <el-tooltip content="Обновить счёт, историю и список проводок" placement="top">
              <el-button
                type="primary"
                icon="el-icon-refresh"
                circle
                size="mini"
                @click="refreshAll"
                :loading="loading"
              ></el-button>
            </el-tooltip>
          </div>
          <div>
            <el-button
              size="small"
              type="primary"
              :loading="verifyLoading"
              @click="verifyFullHistory"
              >Проверить всю историю</el-button
            >
          </div>
          <div v-if="verifyResult" class="reconciliation-result">
            <svg
              v-if="sparklinePoints"
              viewBox="0 0 600 60"
              class="balance-sparkline"
              preserveAspectRatio="none"
            >
              <polyline
                :points="sparklinePoints"
                fill="none"
                stroke="#409EFF"
                stroke-width="2"
              />
            </svg>

            <!-- Баланс: совпадает ли то, что хранится в счёте, с тем, что
            получается при пересчёте по всей истории проводок — это то, на
            что реагирует кнопка "Исправить баланс" ниже. Отдельно и
            независимо от целостности истории ниже: баланс может не совпадать
            даже при полностью согласованной истории (гонка при сохранении
            задела именно закэшированное значение, а не сам лог), и наоборот. -->
            <div class="verify-section verify-section--balance">
              <div class="verify-section__row">
                <i
                  :class="
                    !hasVerifiedData
                      ? 'el-icon-info'
                      : isDeltaNegligible
                      ? 'el-icon-success'
                      : 'el-icon-warning'
                  "
                  :style="{
                    color: !hasVerifiedData
                      ? '#909399'
                      : isDeltaNegligible
                      ? 'rgb(100,200,28)'
                      : 'red'
                  }"
                ></i>
                <span>
                  Баланс в БД:
                  <strong>{{ verifyResult.storedBalance }}</strong> —
                  расчётный по истории проводок:
                  <strong>{{ verifyResult.computedBalance }}</strong> —
                  разница:
                  <strong
                    :style="{
                      color: !hasVerifiedData
                        ? '#909399'
                        : isDeltaNegligible
                        ? 'rgb(100,200,28)'
                        : 'red'
                    }"
                    >{{ verifyResult.delta }}</strong
                  >
                </span>
              </div>
              <div v-if="!hasVerifiedData" class="no-data-note">
                Нет ни одной проводки с записанным балансом после (либо
                истории нет вовсе, либо она вся легаси) — расчётный баланс не
                сверен ни с одной реальной точкой, разница выше не показатель
                реального расхождения
              </div>
              <div
                v-if="isSuperadmin && hasVerifiedData && !isDeltaNegligible"
                class="reconcile-action"
              >
                <el-button
                  size="small"
                  type="warning"
                  :loading="reconcileLoading"
                  @click="reconcileDelta"
                  >Исправить баланс счёта по истории проводок</el-button
                >
              </div>
              <div
                v-if="verifyResult.reconciliations.length"
                class="reconciliation-history"
              >
                <div>История реконсиляций по счёту:</div>
                <ul>
                  <li
                    v-for="r in verifyResult.reconciliations"
                    :key="r.transactionId"
                  >
                    <el-link type="primary" @click="jumpToBreak(r)">
                      {{ $dayjs(r.createdAt).format("DD.MM.YYYY HH:mm:ss") }}
                    </el-link>
                    — исправлен баланс, разница
                    <strong>{{ r.delta }}</strong>
                    ({{ r.creatorLogin || "неизвестно кем" }})
                  </li>
                </ul>
              </div>
              <div v-if="verifyResult.vwap !== null" class="vwap-note">
                Средняя цена приобретения (VWAP):
                <strong
                  >{{ numToString(verifyResult.vwap) }} ₽</strong
                >
                за 1 {{ verifyResult.vwapValuteName }}
                (по {{ verifyResult.vwapVolume }}
                {{ verifyResult.vwapValuteName }} с известным курсом)
                <el-tooltip placement="top">
                  <div slot="content">
                    Курс к рублю сохраняется в проводках с 30.06.2026 — эта
                    величина учитывает только проводки после этой даты, а не
                    всю историю счёта (задним числом курс не восстанавливается).
                  </div>
                  <i class="el-icon-question verify-section__hint"></i>
                </el-tooltip>
              </div>
            </div>

            <!-- Целостность: нет ли необъяснённых скачков МЕЖДУ соседними
            проводками внутри самой истории (легаси-эпоха без записанного
            баланса не в счёт). Отдельный вопрос от баланса выше — см.
            комментарий там же. -->
            <div class="verify-section verify-section--integrity">
              <div class="verify-section__row">
                <i
                  :class="
                    verifyResult.isConsistent
                      ? 'el-icon-success'
                      : 'el-icon-warning'
                  "
                  :style="{
                    color: verifyResult.isConsistent
                      ? 'rgb(100,200,28)'
                      : 'red'
                  }"
                ></i>
                <span>
                  {{
                    verifyResult.isConsistent
                      ? `Целостность истории проводок: разрывов не найдено (проверено ${verifyResult.totalChecked})`
                      : `Целостность истории проводок: найдено разрывов ${verifyResult.breaksTotal} из ${verifyResult.totalChecked} проверенных`
                  }}
                </span>
                <el-tooltip placement="top">
                  <div slot="content">
                    Это отдельная проверка: совпадает ли каждая проводка с
                    предыдущей внутри самой истории. Не то же самое, что баланс
                    выше — разрыв здесь не обязательно означает расхождение
                    баланса, и наоборот.
                  </div>
                  <i class="el-icon-question verify-section__hint"></i>
                </el-tooltip>
              </div>
              <div
                v-if="verifyResult.breaksTotal"
                class="reconciliation-breaks"
              >
                <div>
                  Разрывы (переход к транзакции сбрасывает фильтры и открывает
                  историю с этой даты){{
                    verifyResult.breaksTotal > verifyResult.breaks.length
                      ? ` — показаны первые ${verifyResult.breaks.length} из ${verifyResult.breaksTotal}`
                      : ""
                  }}:
                </div>
                <virtual-list
                  style="max-height: 300px; overflow-y: auto;"
                  :data-key="'transactionId'"
                  :data-sources="verifyResult.breaks"
                  :data-component="breakItemComponent"
                  :extra-props="{ jumpToBreak }"
                />
              </div>
              <el-collapse
                v-if="verifyResult.legacyGapsTotal"
                v-model="legacyGapsActive"
                class="reconciliation-legacy-gaps"
              >
                <el-collapse-item
                  name="1"
                  :title="
                    `Легаси-проводок без записанного баланса: ${verifyResult.legacyGapsTotal} (не в счёт целостности — так помечать перестали после появления системы проводок)` +
                      (verifyResult.legacyGapsTotal > verifyResult.legacyGaps.length
                        ? ` — показаны первые ${verifyResult.legacyGaps.length}`
                        : '')
                  "
                >
                  <!-- Контент раньше рендерился полностью сразу при загрузке
                  (el-collapse-item не откладывает монтирование, просто прячет
                  через CSS) — на счёте с 40к+ легаси-проводок это одно давало
                  десятки тысяч лишних DOM-узлов ещё ДО того, как кто-то вообще
                  открыл блок. v-if держит контент несмонтированным, пока блок
                  реально не раскрыт. -->
                  <virtual-list
                    v-if="legacyGapsActive.includes('1')"
                    style="max-height: 300px; overflow-y: auto;"
                    :data-key="'transactionId'"
                    :data-sources="verifyResult.legacyGaps"
                    :data-component="legacyGapItemComponent"
                    :extra-props="{ jumpToBreak }"
                  />
                </el-collapse-item>
              </el-collapse>
            </div>
          </div>
        </el-card>
        <el-card
          shadow="always"
          :body-style="{ padding: '20px' }"
          v-loading="loading"
        >
          <div slot="header">
            <el-row :gutter="20">
              <el-col :span="20" :offset="0">
                <el-date-picker
                  v-model="filters.dateRange"
                  type="datetimerange"
                  align="right"
                  start-placeholder="От"
                  end-placeholder="До"
                  :default-time="['00:00:00', '23:59:59']"
                  size="mini"
                  @change="getAllData"
                >
                </el-date-picker>
                <span> ИЛИ </span>
                <el-select
                  v-model="filters.createdAtFrom"
                  filterable
                  remote
                  clearable
                  default-first-option
                  placeholder="ID транзакции"
                  :remote-method="queryTransactionSuggestion"
                  :loading="transactionSuggestionsLoading"
                  size="mini"
                  @change="getAllData"
                  @clear="clearTransactionSuggestion"
                >
                  <el-option
                    v-for="item in createdAtFromOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  >
                  </el-option>
                </el-select>
              </el-col>
              <el-col :span="4" :offset="0">
                <!-- <el-button
                  :loading="loading"
                  type="primary"
                  size="mini"
                  @click="getAllData"
                  >Поиск</el-button
                > -->
                <el-button
                  :loading="loading"
                  v-if="hasFilters"
                  type="danger"
                  size="mini"
                  @click="resetFilters"
                  >Сбросить</el-button
                >
                <el-button
                  v-if="!listVisible"
                  size="mini"
                  @click="getAllData"
                  >Показать проводки</el-button
                >
              </el-col>
            </el-row>
          </div>
          <div v-if="!listVisible" class="list-hint">
            Список проводок скрыт — обычно смысла листать его целиком нет.
            Кликните по разрыву в блоке проверки выше, задайте фильтр или
            нажмите «Показать проводки», чтобы увидеть историю.
          </div>
          <template v-else>
            <div v-if="filteredSummary" class="filtered-summary">
              Чистое изменение за выбранный период/фильтр: приход
              {{ filteredSummary.plus }}, расход {{ filteredSummary.minus }},
              итого {{ filteredSummary.total }}
              <span class="batch-size-select">
                Размер пачки подгрузки:
                <el-select
                  v-model="limit"
                  size="mini"
                  @change="handleLimitChange"
                >
                  <el-option
                    v-for="size in pageSizes"
                    :key="size"
                    :value="size"
                    :label="size"
                  ></el-option>
                </el-select>
              </span>
            </div>
            <virtual-list
              ref="virtualList"
              style="max-height: 70vh; overflow-y: auto;"
              :data-key="'_id'"
              :data-sources="allTransactions"
              :data-component="transactionCardComponent"
              :extra-props="{
                hoveredReferenceId,
                setHoveredReference,
                scrollToReference
              }"
              @tobottom="fetchNextBatch"
            />
            <div v-if="loadingMore" class="load-more-status">Загрузка...</div>
            <div
              v-else-if="!hasMore && allTransactions.length"
              class="load-more-status"
            >
              Показаны все проводки ({{ allTransactions.length }} из
              {{ totalDocs }})
            </div>
          </template>
        </el-card>
        <el-collapse :accordion="false">
          <el-collapse-item :key="1" :title="'debug'" :name="1">
            <pre>
              {{ extra }}
            </pre>
            <pre>
              {{ balanceAccount }}
            </pre>
            <pre>
              {{ allTransactions }}
            </pre>
          </el-collapse-item>
        </el-collapse>
      </div>
      <div v-else>
        Произошла ошибка при загрузке данных, пожалуйста попробуйте
        перезагрузить страницу
      </div>
    </div>
  </div>
</template>
<script>
import { mapState } from "vuex";
import {
  getLedgerTransactions,
  getLedgerTransactionsVerify,
  reconcileLedgerTransactions,
  getBalanceAccounts
} from "@/api";
import { calculateStatsSummary } from "@/utils/ledgerStats";
import VirtualList from "vue-virtual-scroll-list";
import TransactionCard from "./components/FullBalance/TransactionCard.vue";
import BreakItem from "./components/FullBalance/BreakItem.vue";
import LegacyGapItem from "./components/FullBalance/LegacyGapItem.vue";
import PageHeader from "../../ui/PageHeader.vue";
const defaultFilters = {
  dateRange: null,
  createdAtFrom: null
};
export default {
  name: "LedgerFullBalance",
  components: { PageHeader,
    "virtual-list": VirtualList
  },
  data: () => ({
    loading: true,
    loaded: false,
    dataLoadFailed: false,
    listVisible: false,
    allTransactions: [],
    hasMore: true,
    loadingMore: false,
    page: 1,
    limit: 100,
    pageSizes: [10, 50, 100, 250, 400, 1000],
    totalDocs: null,
    extra: null,
    balanceAccount: undefined,
    filters: { ...defaultFilters },
    createdAtFromOptions: [],
    transactionSuggestionsLoading: false,
    verifyLoading: false,
    verifyResult: null,
    reconcileLoading: false,
    transactionCardComponent: TransactionCard,
    breakItemComponent: BreakItem,
    legacyGapItemComponent: LegacyGapItem,
    // Активные панели el-collapse легаси-проводок — используется и как
    // v-model, и как флаг "уже раскрывали", чтобы не монтировать
    // virtual-list с потенциально тысячами legacyGaps, пока блок реально не
    // открыт (el-collapse-item сам по себе контент не откладывает).
    legacyGapsActive: [],
    // Id транзакции, на которую сейчас наведена/указана ссылка "Реверсирует
    // ID" из другой строки ленты — прокидывается в каждую карточку через
    // extra-props virtual-list, чтобы подсветить нужную строку саму по себе,
    // а не через события (которые вложенные компоненты не пробрасывают).
    hoveredReferenceId: null,
    referenceHighlightTimeout: null
  }),
  computed: {
    ...mapState("auth", ["session"]),
    // Реконсиляция создаёт необратимую запись в ауди-логе — только
    // суперадмин (type: -1), не обычный оператор (type: 1).
    isSuperadmin() {
      return !!this.session && this.session.type === -1;
    },
    hasFilters() {
      return Object.entries(this.filters).reduce((prev, curr) => {
        return defaultFilters[curr[0]] !== curr[1] || prev;
      }, false);
    },
    filteredSummary() {
      if (!this.extra || !this.extra.totalAmount) return null;
      return calculateStatsSummary(this.extra.totalAmount);
    },
    // Порог должен совпадать с backend DELTA_EPSILON (ledger-transactions/helpers.js):
    // выше "шума" float-суммирования (~1e-10..1e-12), но ниже сатоши (1e-8 BTC) —
    // самой мелкой единицы среди поддерживаемых валют, которая уже деньги, а не шум.
    isDeltaNegligible() {
      return (
        !!this.verifyResult &&
        Math.abs(this.verifyResult.delta || 0) < 0.000000001
      );
    },
    // Если ни одна проводка не несла записанного balanceAccountAfter (нет
    // проводок вовсе либо вся история — легаси-разрывы), computedBalance
    // целиком расчётный и ни разу не сверен с реальной точкой — сравнивать
    // delta с текущим балансом в этом случае бессмысленно, а не "красное".
    hasVerifiedData() {
      return (
        !!this.verifyResult &&
        this.verifyResult.totalChecked > this.verifyResult.legacyGapsTotal
      );
    },
    // Превью формы истории баланса — просто чтобы глаз сразу цеплялся за
    // скачок/провал, не читая карточки одну за другой. Точка на глаз, не
    // точный расчёт (тот делается по полной цепочке на бэкенде).
    sparklinePoints() {
      const points =
        (this.verifyResult && this.verifyResult.balanceHistory) || [];
      if (points.length < 2) return null;
      const values = points.map(p => p.balance);
      const min = Math.min(...values);
      const max = Math.max(...values);
      const range = max - min || 1;
      const w = 600;
      const h = 60;
      const pad = 4;
      return points
        .map((p, i) => {
          const x = pad + (i / (points.length - 1)) * (w - pad * 2);
          const y = h - pad - ((p.balance - min) / range) * (h - pad * 2);
          return `${x.toFixed(2)},${y.toFixed(2)}`;
        })
        .join(" ");
    }
  },
  methods: {
    async fetchNextBatch() {
      if (!this.hasMore || this.loadingMore) return;
      this.loadingMore = true;
      try {
        const query = { balanceAccount: this.$route.params.id };
        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          query.createdAt = {
            $gte: this.filters.dateRange[0],
            $lte: this.filters.dateRange[1]
          };
        }
        if (this.filters.createdAtFrom) {
          query.createdAt = {
            $gte: this.filters.createdAtFrom
          };
        }
        const loadedData = await getLedgerTransactions({
          limit: this.limit,
          page: this.page,
          sort: "createdAt",
          query
        });
        const docs =
          (loadedData.data.result && loadedData.data.result.docs) || [];
        // Метка "новый день" считается один раз здесь (не в самой карточке),
        // потому что карточке нужен контекст предыдущей строки — а список
        // растёт непрерывно (fetchNextBatch дописывает, не заменяет), так что
        // "предыдущая строка" может быть и из уже загруженного ранее батча.
        const docsWithDividers = docs.map((doc, i) => {
          const prevDoc =
            i === 0 ? this.allTransactions[this.allTransactions.length - 1] : docs[i - 1];
          const prevDay =
            prevDoc && prevDoc.createdAt && this.$dayjs(prevDoc.createdAt).format("YYYY-MM-DD");
          const thisDay = doc.createdAt && this.$dayjs(doc.createdAt).format("YYYY-MM-DD");
          return {
            ...doc,
            _dateDivider: thisDay && thisDay !== prevDay ? thisDay : null
          };
        });
        this.allTransactions = [...this.allTransactions, ...docsWithDividers];
        this.totalDocs =
          loadedData.data.result && loadedData.data.result.totalDocs;
        this.extra = loadedData.data.result && loadedData.data.extra;
        this.page += 1;
        this.hasMore = this.allTransactions.length < (this.totalDocs || 0);
      } catch (error) {
        if (!this.allTransactions.length) this.dataLoadFailed = true;
        this.handleResponseError(error);
      } finally {
        this.loadingMore = false;
      }
      // Если после подгрузки список всё ещё не заполнил видимую область —
      // прокручивать нечего, "@tobottom" никогда не сработает сам. Дозагружаем.
      if (this.hasMore) {
        await this.$nextTick();
        const el = this.$refs.virtualList && this.$refs.virtualList.$el;
        if (el && el.scrollHeight <= el.clientHeight) {
          await this.fetchNextBatch();
        }
      }
    },
    async handleLimitChange() {
      await this.getAllData();
    },
    async getBalanceAccount(id = this.$route.params.id) {
      const { data } = await getBalanceAccounts({
        query: { _id: id }
      });
      this.balanceAccount = data.result && data.result.docs;
    },
    // Сводка + проверка целостности — то же самое, что грузится при первом
    // mounted(), вынесено отдельно, чтобы этим же путём можно было
    // перезагрузиться при смене :id без полного размонтирования компонента
    // (см. beforeRouteUpdate). Ленту проводок (getAllData) сюда намеренно не
    // включаем — она и на первом заходе на страницу грузится только по
    // кнопке «Показать проводки», а не сразу.
    async loadAccount(id) {
      this.loading = true;
      try {
        await this.getBalanceAccount(id);
        await this.verifyFullHistory();
      } catch (error) {
        console.log(error);
        this.handleResponseError(error);
      } finally {
        this.loading = false;
        this.loaded = true;
      }
    },
    // Сбрасывает всё, что относится к ПРЕДЫДУЩЕМУ счёту, перед переходом на
    // новый — иначе, например, лента проводок или фильтры одного счёта
    // на секунду-другую показывались бы под шапкой уже другого счёта, пока
    // не отработает loadAccount().
    resetAccountState() {
      this.balanceAccount = undefined;
      this.filters = { ...defaultFilters };
      this.createdAtFromOptions = [];
      this.allTransactions = [];
      this.listVisible = false;
      this.dataLoadFailed = false;
      this.page = 1;
      this.hasMore = true;
      this.extra = null;
      this.totalDocs = null;
      this.verifyResult = null;
      this.legacyGapsActive = [];
      this.hoveredReferenceId = null;
      clearTimeout(this.referenceHighlightTimeout);
    },
    async getAllData() {
      try {
        this.loading = true;
        this.dataLoadFailed = false;
        this.listVisible = true;
        this.allTransactions = [];
        this.page = 1;
        this.hasMore = true;
        await this.fetchNextBatch();
        await this.getBalanceAccount();
      } catch (error) {
        console.log(error);
        this.handleResponseError(error);
      } finally {
        this.loading = false;
      }
    },
    async refreshAll() {
      this.loading = true;
      try {
        await this.getBalanceAccount();
        await this.verifyFullHistory();
        if (this.listVisible) {
          await this.getAllData();
        }
      } finally {
        this.loading = false;
      }
    },
    async resetFilters() {
      this.filters = { ...defaultFilters };
      await this.getAllData();
    },
    async verifyFullHistory() {
      if (!this.balanceAccount || !this.balanceAccount[0]) return;
      this.verifyLoading = true;
      try {
        const { data } = await getLedgerTransactionsVerify({
          balanceAccount: this.balanceAccount[0]._id
        });
        if (data.status) {
          this.verifyResult = data.result;
        }
      } catch (error) {
        this.handleResponseError(error);
      } finally {
        this.verifyLoading = false;
      }
    },
    async reconcileDelta() {
      if (!this.balanceAccount || !this.balanceAccount[0]) return;
      try {
        await this.$confirm(
          `Баланс счёта будет исправлен с ${this.verifyResult.storedBalance} на расчётный ${this.verifyResult.computedBalance} (разница ${this.verifyResult.delta}). Действие необратимо. Продолжить?`,
          "Внимание!",
          {
            confirmButtonText: "Создать",
            cancelButtonText: "Отменить",
            type: "warning"
          }
        );
      } catch (error) {
        return;
      }
      this.reconcileLoading = true;
      try {
        const { data } = await reconcileLedgerTransactions(
          this.balanceAccount[0]._id
        );
        if (data.status) {
          this.verifyResult = data.result;
          this.$message({ type: "success", message: "Баланс счёта исправлен!" });
        }
      } catch (error) {
        this.handleResponseError(error);
      } finally {
        this.reconcileLoading = false;
      }
    },
    setHoveredReference(id) {
      this.hoveredReferenceId = id;
    },
    // Клик по "Реверсирует ID" — прокручивает ленту к этой транзакции, если
    // она уже загружена (в текущей отфильтрованной подгруженной части
    // allTransactions). Если её там нет (не загружена ещё или отфильтрована)
    // — честно говорим, что нечего прокручивать, вместо тихого бездействия.
    scrollToReference(id) {
      if (!id) return;
      const idx = this.allTransactions.findIndex(t => t._id === id);
      if (idx === -1) {
        this.$message({
          type: "warning",
          message: "Эта транзакция не загружена в текущую ленту (другой фильтр или ещё не подгружена)"
        });
        return;
      }
      if (this.$refs.virtualList) this.$refs.virtualList.scrollToIndex(idx);
      this.hoveredReferenceId = id;
      clearTimeout(this.referenceHighlightTimeout);
      this.referenceHighlightTimeout = setTimeout(() => {
        this.hoveredReferenceId = null;
      }, 2500);
    },
    async jumpToBreak(breakItem) {
      // Разрыв — это несостыковка МЕЖДУ этой и предыдущей транзакцией, поэтому
      // переходим на предыдущую, чтобы видеть обе стороны разрыва, а не только
      // "сломанную" транзакцию саму по себе.
      const jumpTarget = breakItem.previousCreatedAt || breakItem.createdAt;
      const jumpLabel = breakItem.previousTransactionId
        ? `${breakItem.previousTransactionId} ${breakItem.previousCreatedAt}`
        : `${breakItem.transactionId} ${breakItem.createdAt}`;
      this.filters = { ...defaultFilters, createdAtFrom: jumpTarget };
      this.createdAtFromOptions = [
        {
          value: jumpTarget,
          label: jumpLabel
        }
      ];
      await this.getAllData();
    },
    clearTransactionSuggestion() {
      this.filters.createdAtFrom = null;
    },
    async queryTransactionSuggestion(queryString) {
      if (queryString.length !== 24) return (this.createdAtFromOptions = []);
      try {
        this.transactionSuggestionsLoading = true;
        const loadedData = await getLedgerTransactions({
          limit: 10,
          page: 1,
          sort: "createdAt",
          query: { _id: queryString }
        });
        this.createdAtFromOptions = loadedData.data.result.docs.map(tr => {
          return {
            value: tr.createdAt,
            label: `${tr._id} ${tr.createdAt}`
          };
        });
      } catch (error) {
        console.log(error);
      } finally {
        this.transactionSuggestionsLoading = false;
      }
    }
  },
  async mounted() {
    await this.loadAccount(this.$route.params.id);
  },
  // Vue Router переиспользует этот же компонент при переходе между
  // /ledger/fullbalance/:id с разными id (это не разные компоненты — не
  // размонтируется и не монтируется заново), так что mounted() тут не
  // перевызовется сам по себе. Без этого клик по уведомлению/ссылке на
  // другой счёт молча менял бы URL, оставляя на экране данные старого
  // счёта. to.params.id берём явно, а не через this.$route — на момент
  // вызова этого guard'а this.$route ещё указывает на СТАРЫЙ роут.
  async beforeRouteUpdate(to, from, next) {
    next();
    if (to.params.id === from.params.id) return;
    this.resetAccountState();
    await this.loadAccount(to.params.id);
  },
  beforeDestroy() {
    clearTimeout(this.referenceHighlightTimeout);
  }
};
</script>
<style scoped>
.transaction-info {
  color: #aaa;
  margin-left: 1rem;
  font-size: 0.85rem;
}
.transaction-info i {
  margin-right: 5px;
  color: hsl(210, 50%, 63%);
}
.reconciliation-card {
  margin-bottom: 20px;
}
.reconciliation-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.reconciliation-result {
  margin-top: 10px;
}
.verify-section {
  padding: 10px 0;
}
.verify-section--balance {
  padding-top: 0;
}
.verify-section--integrity {
  border-top: 1px solid #ebeef5;
  margin-top: 5px;
}
.verify-section__row {
  display: flex;
  align-items: baseline;
  gap: 6px;
}
.verify-section__row i {
  flex-shrink: 0;
}
.verify-section__hint {
  color: #909399;
  cursor: help;
}
.balance-sparkline {
  width: 100%;
  height: 60px;
  display: block;
  margin-bottom: 10px;
}
.reconciliation-history {
  margin-top: 10px;
  max-height: 200px;
  overflow-y: auto;
}
.reconciliation-history ul {
  padding-left: 20px;
  margin: 5px 0;
}
.reconciliation-breaks {
  margin-top: 10px;
  max-height: 300px;
  overflow-y: auto;
}
.reconciliation-breaks ul {
  padding-left: 20px;
  margin: 5px 0;
}
.first-tx-note {
  color: #909399;
  font-size: 0.85rem;
}
.no-data-note {
  color: #909399;
  font-size: 0.85rem;
  margin-top: 5px;
}
.vwap-note {
  color: #606266;
  font-size: 0.85rem;
  margin-top: 10px;
}
.reconcile-action {
  margin-top: 10px;
}
.reconciliation-legacy-gaps {
  margin-top: 10px;
}
.reconciliation-legacy-gaps ul {
  max-height: 300px;
  overflow-y: auto;
  padding-left: 20px;
  margin: 5px 0;
}
.filtered-summary {
  margin: 10px 0;
  color: #606266;
  font-size: 0.9rem;
}
.batch-size-select {
  margin-left: 20px;
}
.batch-size-select .el-select {
  width: 90px;
}
.load-more-status {
  text-align: center;
  color: #909399;
  padding: 10px 0;
  font-size: 0.85rem;
}
.list-hint {
  color: #909399;
  font-size: 0.9rem;
  padding: 10px 0;
}
</style>



// WEBPACK FOOTER //
// src/components/pages/Ledger/fullbalance.vue